/**
 * Excel export helper functions
 *
 * These utilities consolidate common Excel export patterns to reduce code duplication.
 * Most exports follow a similar pattern: create workbook, add worksheet, set columns,
 * style headers, add data, and save.
 */

import { saveAs } from "file-saver";
import { createWorkbook, Workbook, Worksheet } from "./lazyExcel";
import type { Column } from "exceljs";

/**
 * Options for styling worksheets
 */
export interface WorksheetStyleOptions {
  /** Center-align header row */
  centerHeaders?: boolean;
  /** Freeze the header row */
  freezeHeader?: boolean;
  /** Custom sheet name */
  sheetName?: string;
}

/**
 * Save a workbook to a file
 * Handles the conversion to Blob and triggers download
 */
export async function saveWorkbook(workbook: Workbook, fileName: string): Promise<void> {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const fullFileName = fileName.endsWith(".xlsx") ? fileName : `${fileName}.xlsx`;
  saveAs(blob, fullFileName);
}

/**
 * Style the header row of a worksheet
 */
export function styleHeaderRow(worksheet: Worksheet, options?: { center?: boolean }): void {
  if (options?.center !== false) {
    worksheet.getRow(1).alignment = { horizontal: "center", vertical: "middle" };
  }
}

/**
 * Freeze the header row of a worksheet
 */
export function freezeHeaderRow(worksheet: Worksheet): void {
  worksheet.views = [{ state: "frozen", xSplit: undefined, ySplit: 1 }];
}

/**
 * Create a simple Excel export with common defaults
 *
 * This covers ~80% of export use cases:
 * - Single worksheet
 * - Column definitions with headers
 * - Centered/styled header row
 * - Optional frozen header
 * - Data rows
 *
 * @example
 * await createSimpleExport({
 *   columns: [
 *     { header: "Name", key: "name", width: 20 },
 *     { header: "Email", key: "email", width: 30 }
 *   ],
 *   rows: users.map(u => ({ name: u.name, email: u.email })),
 *   fileName: "users-export",
 *   options: { centerHeaders: true, freezeHeader: true }
 * });
 */
export async function createSimpleExport<T = any>({
  columns,
  rows,
  fileName,
  options = {},
}: {
  columns: Partial<Column>[];
  rows: T[];
  fileName: string;
  options?: WorksheetStyleOptions;
}): Promise<void> {
  const workbook = await createWorkbook();
  const worksheet = workbook.addWorksheet(options.sheetName);

  // Set columns
  worksheet.columns = columns;

  // Style header
  if (options.centerHeaders !== false) {
    styleHeaderRow(worksheet);
  }

  // Freeze header
  if (options.freezeHeader) {
    freezeHeaderRow(worksheet);
  }

  // Add data
  worksheet.addRows(rows);

  // Save
  await saveWorkbook(workbook, fileName);
}

/**
 * Create a workbook and worksheet with common setup
 * Returns both for advanced customization before saving
 *
 * Use this when you need more control than createSimpleExport provides,
 * but still want the common boilerplate handled.
 *
 * @example
 * const { workbook, worksheet } = await createWorksheet({
 *   columns: exportColumns,
 *   options: { centerHeaders: true, freezeHeader: true }
 * });
 *
 * // Custom manipulation
 * worksheet.getRow(2).font = { bold: true };
 *
 * // Add data and save
 * worksheet.addRows(rows);
 * await saveWorkbook(workbook, fileName);
 */
export async function createWorksheet({
  columns,
  options = {},
}: {
  columns: Partial<Column>[];
  options?: WorksheetStyleOptions;
}): Promise<{ workbook: Workbook; worksheet: Worksheet }> {
  const workbook = await createWorkbook();
  const worksheet = workbook.addWorksheet(options.sheetName);

  worksheet.columns = columns;

  if (options.centerHeaders !== false) {
    styleHeaderRow(worksheet);
  }

  if (options.freezeHeader) {
    freezeHeaderRow(worksheet);
  }

  return { workbook, worksheet };
}
